import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import Layout from "../components/layout/Layout";
import products from "../data/products";
import useSeo from "../hooks/useSeo";

const ProductSection = ({ product, index }) => {
  const reverse = index % 2 === 1;
  return (
    <section
      id={product.slug}
      data-testid={`produkt-section-${product.slug}`}
      className="scroll-mt-24 py-12 md:py-16 border-t border-[color:hsl(var(--line))]"
    >
      <div className="container-vido">
        <div
          className={`grid gap-8 lg:gap-12 lg:grid-cols-12 items-center ${
            reverse ? "lg:[&>div:first-child]:order-2" : ""
          }`}
        >
          <div className="lg:col-span-6 fade-in">
            <div className="aspect-[5/4] overflow-hidden rounded-[24px] border border-[color:hsl(var(--line))] bg-[hsl(var(--card-bg))]">
              <img
                src={product.image}
                alt={`${product.name} – ${product.category} från Vido Klimatteknik`}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="lg:col-span-6 fade-in">
            <span className="eyebrow text-[color:hsl(var(--accent-terracotta))]">
              {product.category}
            </span>
            <h2
              className="mt-3 font-serif text-3xl md:text-4xl font-medium leading-[1.05]"
              data-testid={`produkt-name-${product.slug}`}
            >
              {product.name}
            </h2>
            <p className="mt-4 text-[color:hsl(var(--muted-ink))] text-base md:text-[17px] leading-relaxed max-w-[58ch]">
              {product.shortDescription}
            </p>

            <div className="mt-5">
              <div className="text-sm font-semibold text-[color:hsl(var(--ink))] mb-1">
                För vem?
              </div>
              <p className="text-sm text-[color:hsl(var(--muted-ink))] leading-relaxed max-w-[58ch]">
                {product.forWhom}
              </p>
            </div>

            <ul className="mt-5 space-y-2">
              {product.benefits.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm">
                  <Check
                    size={16}
                    className="mt-0.5 flex-shrink-0 text-[color:hsl(var(--accent-terracotta))]"
                  />
                  <span className="text-[color:hsl(var(--ink))]">{b}</span>
                </li>
              ))}
            </ul>

            <div className="my-6 h-px bg-[color:hsl(var(--line))]" />

            <h3 className="font-serif text-xl font-medium">Det här ingår</h3>
            <ul className="mt-3 space-y-2">
              {product.included.map((i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Check
                    size={16}
                    className="mt-0.5 flex-shrink-0 text-[color:hsl(var(--accent-terracotta))]"
                  />
                  <span className="text-[color:hsl(var(--ink))]">{i}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex flex-wrap items-end gap-5">
              <div>
                {product.priceType === "från" && (
                  <span className="text-xs text-[color:hsl(var(--muted-ink))] block uppercase tracking-widest">
                    Från
                  </span>
                )}
                <div
                  className="font-serif text-3xl md:text-4xl font-medium text-[color:hsl(var(--ink))] leading-none"
                  data-testid={`produkt-price-${product.slug}`}
                >
                  {product.price}
                </div>
                <div className="text-sm text-[color:hsl(var(--muted-ink))] mt-2">
                  {product.priceNote}
                </div>
              </div>
              <Link
                to={`/kontakt?produkt=${product.slug}`}
                data-testid={`produkt-cta-${product.slug}`}
                className="btn-primary"
              >
                Jag är intresserad
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Produkter() {
  useSeo({
    title: "Produkter & priser | Vido Klimatteknik",
    description:
      "Fem lösningar med fast pris efter ROT \u2014 luft/luftvärmepump, frånluftsvärmepump, varmvattenberedare, AC och ventilation.",
    path: "/produkter",
  });
  return (
    <Layout>
      {/* Compact hero */}
      <section
        data-testid="produkter-hero"
        className="pt-12 md:pt-16 pb-8 md:pb-10"
      >
        <div className="container-vido max-w-3xl fade-in">
          <span className="eyebrow">Produkter</span>
          <h1 className="display-h1 mt-5">
            Fem lösningar med{" "}
            <span className="italic text-[color:hsl(var(--accent-terracotta))]">
              fast pris
            </span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[color:hsl(var(--muted-ink))] max-w-[58ch] leading-relaxed">
            Varje paket är samman­satt så att du direkt ser vad du får — och
            vad det kostar. Standardinstallation ingår alltid, och det du ser
            är priset efter ROT-avdrag.
          </p>
        </div>
      </section>

      <div>
        {products.map((p, i) => (
          <ProductSection key={p.slug} product={p} index={i} />
        ))}
      </div>

      {/* Disclaimer */}
      <section className="py-5 md:py-6 border-t border-[color:hsl(var(--line))]">
        <div className="container-vido">
          <p
            data-testid="produkter-disclaimer"
            className="text-xs md:text-sm text-[color:hsl(var(--muted-ink))] max-w-[70ch] leading-relaxed"
          >
            Priserna gäller standardinstallation efter ROT. Slutligt pris
            bekräftas alltid innan arbetet påbörjas.
          </p>
        </div>
      </section>
    </Layout>
  );
}
